import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, ErreurApi, lire } from '../api'
import {
  type AccesRecu,
  DUREES_ACCES,
  libelleDuree,
  LIBELLES_STATUT_ACCES,
  requeteAccesTemporaires,
} from '../composants/AccesTemporaires'
import { formaterInstant } from '../composants/Historique'
import { Section, Vide } from '../composants/Section'
import { EntetePage } from '../composants/Ui'
import { confirmer } from '../composants/confirmation'

// Accès temporaires (invariant I4). Chez la titulaire d'un espace privé : les demandes des
// administrateurs à accepter (lecture seule, 48 h au plus) ou refuser, les accès en cours à
// révoquer, l'historique. Chez un administrateur : où en sont ses demandes. Chaque geste est
// journalisé sur l'espace.

const instant = (v: unknown) => (v ? formaterInstant(String(v)) : '')

function Recu({ a }: { a: AccesRecu }) {
  const client = useQueryClient()
  const [duree, setDuree] = useState<number>(4)
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  async function geste(f: () => Promise<unknown>) {
    setEnvoi(true)
    setErreur(null)
    try {
      await f()
    } catch (x) {
      setErreur(x instanceof ErreurApi ? x.message : 'Action impossible.')
    } finally {
      setEnvoi(false)
      await client.invalidateQueries({ queryKey: ['acces-temporaires'] })
    }
  }
  const param = { id: a.id }

  return (
    <div className="ligne">
      <div className="ligne-contenu">
        <strong>{a.beneficiaire}</strong> · {a.espace}
        <div>« {a.motif} »</div>
        <div className="discret">
          Demandé le {instant(a.demandeLe)}
          {a.statut === 'accorde' && ` · lecture seule jusqu’au ${instant(a.fin)}`}
          {a.statut !== 'demande' && a.statut !== 'accorde' && (
            <>
              {' '}
              · {LIBELLES_STATUT_ACCES[a.statut]}
              {/* Échu : sa fin ; refusé ou révoqué : l'instant du geste. */}
              {a.statut === 'expire'
                ? a.fin
                  ? ` le ${instant(a.fin)}`
                  : ''
                : a.decideLe
                  ? ` le ${instant(a.decideLe)}`
                  : ''}
            </>
          )}
        </div>
        {erreur && <p className="alerte">{erreur}</p>}
      </div>
      {a.statut === 'demande' && (
        <div className="ligne-actions">
          <label className="champ">
            <span>Durée</span>
            <select value={duree} onChange={(e) => setDuree(Number(e.target.value))}>
              {DUREES_ACCES.map((h) => (
                <option key={h} value={h}>
                  {libelleDuree(h)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="bouton"
            disabled={envoi}
            onClick={() =>
              geste(() =>
                lire(
                  api['acces-temporaires'][':id'].accorder.$post({
                    param,
                    json: { dureeHeures: duree },
                  }),
                ),
              )
            }
          >
            Accepter
          </button>
          <button
            type="button"
            className="bouton-discret"
            disabled={envoi}
            onClick={() =>
              geste(() => lire(api['acces-temporaires'][':id'].refuser.$post({ param })))
            }
          >
            Refuser
          </button>
        </div>
      )}
      {a.statut === 'accorde' && (
        <div className="ligne-actions">
          <button
            type="button"
            className="bouton"
            disabled={envoi}
            onClick={async () => {
              if (!(await confirmer(`Mettre fin maintenant à l’accès de ${a.beneficiaire} ?`)))
                return
              void geste(() => lire(api['acces-temporaires'][':id'].revoquer.$post({ param })))
            }}
          >
            Révoquer
          </button>
        </div>
      )}
    </div>
  )
}

export function AccesTemporaires() {
  const acces = useQuery(requeteAccesTemporaires)
  const d = acces.data
  const recues = d?.recues ?? []
  const actives = recues.filter((a) => a.statut === 'demande' || a.statut === 'accorde')
  const passees = recues.filter((a) => a.statut !== 'demande' && a.statut !== 'accorde')
  return (
    <main>
      <EntetePage surtitre="Espace privé" titre="Accès temporaires" />
      {acces.error && <p className="alerte">{acces.error.message}</p>}
      {d && d.espaces.length > 0 && (
        <>
          <Section titre="Demandes et accès en cours">
            <p className="discret">
              Un administrateur ne voit pas votre espace ({d.espaces.map((e) => e.nom).join(', ')}
              ). Il peut vous demander à le consulter : accepté, il lit sans rien modifier, jusqu’à
              l’heure choisie ou jusqu’à ce que vous y mettiez fin.
            </p>
            {actives.length === 0 && <Vide>Aucune demande en attente, aucun accès en cours.</Vide>}
            {actives.map((a) => (
              <Recu key={a.id} a={a} />
            ))}
          </Section>
          {passees.length > 0 && (
            <Section titre="Historique">
              {passees.map((a) => (
                <Recu key={a.id} a={a} />
              ))}
            </Section>
          )}
        </>
      )}
      {d && d.envoyees.length > 0 && (
        <Section titre="Mes demandes">
          {d.envoyees.map((a) => (
            <div key={a.id} className="ligne">
              <div className="ligne-contenu">
                <strong>{a.espace}</strong> · {LIBELLES_STATUT_ACCES[a.statut]}
                <div>« {a.motif} »</div>
                <div className="discret">
                  Demandé le {instant(a.demandeLe)}
                  {a.statut === 'accorde' && ` · lecture seule jusqu’au ${instant(a.fin)}`}
                </div>
              </div>
            </div>
          ))}
        </Section>
      )}
      {d && d.espaces.length === 0 && d.envoyees.length === 0 && (
        <Vide>Aucun accès temporaire vous concernant.</Vide>
      )}
    </main>
  )
}
